import React from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { ShipperForm } from '@/services/shipper/schemas'

import styled from 'styled-components'
import Text from '@/components/content/Text'
import Input from '@/components/content/Input'
import { VStack, HStack } from '@musinsa/mwms-ds'
import { FormField } from '@/components/content/FormField'
import { colorSystem } from '@/styles/theme'
import { DATE_FORMAT } from '@/utils/consts'
import dayjs from 'dayjs'

interface Props {
  form: UseFormReturn<ShipperForm>
}

/** MEMO: MFS 유형만 FormField 배치가 달라서 따로 분리함. */
function MFSShipperForm({ form }: Props) {
  const { control, getValues, formState } = form

  return (
    <div>
      <HStack>
        <FormField label="화주 유형">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            MFS
          </Text>
        </FormField>
        <FormField label="화주 코드">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('shipperCode')}
          </Text>
        </FormField>
      </HStack>

      <HStack>
        <FormField label="화주명">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('shipperName')}
          </Text>
        </FormField>
        <FormField label="상호명">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('companyName')}
          </Text>
        </FormField>
      </HStack>
      <HStack>
        <FormField label="사업자등록번호">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('businessRegistrationNumber')}
          </Text>
        </FormField>
        <FormField label="대표자명">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('ownerName')}
          </Text>
        </FormField>
      </HStack>
      <HStack>
        <FormField label="판매플랫폼">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('salesPlatforms')?.[0]?.name || ''}
          </Text>
        </FormField>
        <FormField label="업체코드">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('salesPlatforms')?.[0]?.masterCompanyId || ''}
          </Text>
        </FormField>
      </HStack>
      <HStack>
        <FormField label="주문수집여부">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {getValues('salesPlatforms')?.[0]?.usable ? 'Y' : 'N'}
          </Text>
        </FormField>
        <FormField label="약관동의 수집 일시">
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            {dayjs(getValues('mfsTermsAgreedAt')).format(DATE_FORMAT.date) || ''}
          </Text>
        </FormField>
      </HStack>
      <FormField label="업태">
        <Controller
          name="businessType"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="업태 입력 (ex.소매업)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>
      <FormField label="업종">
        <Controller
          name="businessMajor"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="업종 입력 (ex.의류)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>
      <FormField label="주소">
        <VStack gap={8}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <FixedWidthInput {...field} placeholder="우편번호 입력" value={field.value || ''} />
                {fieldState.error && (
                  <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                    {fieldState.error.message}
                  </ErrorMessage>
                )}
              </>
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Input {...field} placeholder="상세주소 입력" value={field.value || ''} />
                {fieldState.error && (
                  <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                    {fieldState.error.message}
                  </ErrorMessage>
                )}
              </>
            )}
          />
        </VStack>
      </FormField>
      <FormField label="전화번호">
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="전화번호 입력 (- 포함)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>
    </div>
  )
}

export default MFSShipperForm

const ErrorMessage = styled(Text)`
  margin-top: 2px;
  margin-left: 4px;
`

const FixedWidthInput = styled(Input)`
  width: 240px;
`
